import { WorkLog } from "@wingspanhq/wingspan-api-sdk";
import { useEffect, useMemo, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { queriesWorkLogs } from "@query";
import { useSession } from "@hooks/useSession";
import { useQueryWorkLogsWithPayees } from "../../../query/workLog/queries/useQueryWorkLogsWithPayees";
import { useQueryWorkLogsSummary } from "../../../query/workLog/queries/useQueryWorkLogsSummary";
import { useSetWSStore } from "../../../store";
import { BulkReviewProps } from "../../BulkReview/types";
import { selectorSearchRowName } from "../../../shared/selectors/selectorSearchRowName";
import { routeHelpersWorkLogPayerDetails } from "../paths";
import { createListRequest } from "../components/TableWorkLogsPayerView/filters/request";
import { WorkLogPayerTabFilterStatus } from "../components/TableWorkLogsPayerView/filters/types";

export function useBulkReviewWorkLogs(workLogId?: string) {
  const history = useHistory();
  const location = useLocation();
  const setStore = useSetWSStore();
  const { accountId } = useSession();

  // Activate only when ?bulkReview=true is in the URL
  const isActive =
    new URLSearchParams(location.search).get("bulkReview") === "true";

  const request = useMemo(
    () =>
      createListRequest(accountId, {
        status: WorkLogPayerTabFilterStatus.ToApprove
      }),
    [accountId]
  );

  const queryWorkLogs = useQueryWorkLogsWithPayees(request, {
    joinPayee: true,
    enabled: isActive,
    refetchOnMount: true
  });

  const queryWorkLogsSummary = useQueryWorkLogsSummary(request, {
    enabled: isActive
  });

  const workLogsData = queryWorkLogs.data;
  const workLogs = useMemo(() => workLogsData || [], [workLogsData]);

  const bulkReviewProps = useMemo((): BulkReviewProps<WorkLog> => {
    return {
      activeId: workLogId,
      listSize: queryWorkLogsSummary.data?.listSize,
      rows: workLogs.map(wl => {
        const payee = wl.payee;
        const payeeName = payee ? selectorSearchRowName(payee) : undefined;
        return {
          id: wl.id!,
          text: payeeName || `Work log #${wl.workLogNumber}`,
          secondaryText: `Work log #${wl.workLogNumber}`,
          // TODO(v3): v3 WorkLog has no top-level `updatedAt`; approximate with
          // the in-review event timestamp.
          updatedAt: wl.events?.inReviewAt,
          data: wl
        };
      }),
      onRowClick: row => {
        const searchParams = Object.fromEntries(
          new URLSearchParams(location.search)
        ) as { bulkReview: string };
        history.push(
          routeHelpersWorkLogPayerDetails.createLocationObject(
            { workLogId: row.id },
            { search: searchParams }
          )
        );
      },
      fetchNextPage: queryWorkLogs.fetchNextPage,
      hasNextPage:
        !queryWorkLogs.isFetchingNextPage && queryWorkLogs.hasNextPage,
      onClose: () => {
        const params = new URLSearchParams(location.search);
        params.delete("bulkReview");
        const newSearch = params.toString();
        history.replace({ search: newSearch ? `?${newSearch}` : "" });
      }
    };
  }, [
    workLogId,
    workLogs,
    queryWorkLogsSummary.data?.listSize,
    queryWorkLogs.fetchNextPage,
    queryWorkLogs.hasNextPage,
    queryWorkLogs.isFetchingNextPage,
    history,
    location.search
  ]);

  // Show sidebar when active and has multiple items
  useEffect(() => {
    if (!isActive) return;
    setStore({ bulkReviewVisible: workLogs.length > 1 });
    return () => {
      setStore({ bulkReviewVisible: false });
    };
  }, [isActive, workLogs.length, setStore]);

  // Update bulk review data in store.
  // useQueryWorkLogs runs flatten() on every render, so bulkReviewProps is always
  // a new reference. Guard with a simple content check to prevent re-render loops.
  const prevRowCount = useRef(-1);
  const prevActiveId = useRef(workLogId);
  useEffect(() => {
    if (!isActive) return;
    if (
      bulkReviewProps.rows.length === prevRowCount.current &&
      bulkReviewProps.activeId === prevActiveId.current
    ) {
      return;
    }
    prevRowCount.current = bulkReviewProps.rows.length;
    prevActiveId.current = bulkReviewProps.activeId;
    setStore({ bulkReview: bulkReviewProps });
  }, [isActive, bulkReviewProps, setStore]);

  // Prefetch adjacent work logs for faster navigation
  const activeIndex = workLogs.findIndex(wl => wl.id === workLogId);
  const prevWorkLog = activeIndex > 0 ? workLogs[activeIndex - 1] : undefined;
  const nextWorkLog =
    activeIndex < workLogs.length - 1 ? workLogs[activeIndex + 1] : undefined;

  queriesWorkLogs.getWorkLog.useQuery(
    { workLogId: prevWorkLog?.id ?? "" },
    { enabled: !!prevWorkLog, staleTime: Infinity }
  );
  queriesWorkLogs.getWorkLog.useQuery(
    { workLogId: nextWorkLog?.id ?? "" },
    { enabled: !!nextWorkLog, staleTime: Infinity }
  );

  return { queryWorkLogs };
}
